// =============================================================================
// FlashlightTile — Dashboard activation tile for Night Operations Mode
// Animated flashlight button with breathing glow when standby, active when on.
// =============================================================================

import { type JSX } from 'react';
import { useNightOpsStore } from '../../stores/nightOps.store.js';
import { Flashlight } from 'lucide-react';

interface FlashlightTileProps {
  onOpenPanel?: () => void;
}

export function FlashlightTile({ onOpenPanel }: FlashlightTileProps): JSX.Element {
  const { active, toggle, preset } = useNightOpsStore();

  return (
    <div
      data-testid="flashlight-tile"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderRadius: 12,
        background: active
          ? 'rgba(251,191,36,0.08)'
          : 'rgba(255,255,255,0.025)',
        border: active
          ? '1px solid rgba(251,191,36,0.45)'
          : '1px solid rgba(40,68,116,0.5)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        animation: active
          ? 'flashlight-active 2s ease-in-out infinite'
          : 'flashlight-breathe 3s ease-in-out infinite',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={toggle}
      title={active ? 'Night Operations Mode — ACTIVE. Click to deactivate.' : 'Activate Night Operations Mode'}
    >
      {/* Shine effect when active */}
      {active && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(105deg, transparent 35%, rgba(251,191,36,0.04) 50%, transparent 65%)',
          animation: 'lm-shine 3s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
      )}

      {/* Flashlight icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: active ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.04)',
        border: active ? '1px solid rgba(251,191,36,0.4)' : '1px solid rgba(40,68,116,0.4)',
        transition: 'all 0.3s',
        filter: active ? 'drop-shadow(0 0 8px rgba(251,191,36,0.8))' : 'none',
      }}>
        <Flashlight
          className="h-5 w-5"
          style={{ color: active ? '#FBbf24' : '#475569', transition: 'color 0.3s' }}
          strokeWidth={1.75}
        />
      </div>

      {/* Label */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
          color: active ? '#FCD34D' : '#94A3B8',
          transition: 'color 0.3s',
        }}>
          Night Ops {active ? '— ACTIVE' : 'Mode'}
        </div>
        <div style={{ fontSize: 10, color: active ? 'rgba(251,191,36,0.6)' : '#475569', marginTop: 1 }}>
          {active ? `Preset: ${preset.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}` : 'Low-light · Film · Field · Blueprint'}
        </div>
      </div>

      {/* Settings gear */}
      {active && onOpenPanel && (
        <button
          data-testid="night-ops-settings-btn"
          onClick={(e) => { e.stopPropagation(); onOpenPanel(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
            borderRadius: 6, cursor: 'pointer',
            background: 'rgba(251,191,36,0.08)',
            border: '1px solid rgba(251,191,36,0.25)',
            color: 'rgba(251,191,36,0.7)', fontSize: 10, fontWeight: 600,
            flexShrink: 0,
          }}
        >
          Settings
        </button>
      )}

      {/* Status dot */}
      <div style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
        background: active ? '#FBbf24' : '#1E293B',
        boxShadow: active ? '0 0 6px rgba(251,191,36,0.9)' : 'none',
        transition: 'all 0.3s',
      }} />
    </div>
  );
}
